// Argus — WindowManager: container global care render-uiește toate FloatingWindow active.
// Citește windowStack din Zustand store + găsește metadata agent + alege FloatingWindow content per panelType.
// Faza 3 = paneluri stub (placeholder text). Faza 4-5 înlocuiesc stub-urile cu paneluri reale.

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import SettingsIcon from '@mui/icons-material/Settings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BuildIcon from '@mui/icons-material/Build';
import TerminalIcon from '@mui/icons-material/Terminal';
import { fetchAgents, type Agent } from '../../lib/api';
import type { PanelType } from '../../lib/layoutPrefs';
import { useWindowStore } from '../../store/windowStore';
import { FloatingWindow } from './FloatingWindow';
import { FileExplorerWindow } from '../../panels/FileExplorerWindow';
import { DesktopWindow } from '../../panels/DesktopWindow';
import { WebcamWindow } from '../../panels/WebcamWindow';
import { SettingsWindow } from '../../panels/SettingsWindow';
import { SystemInfoWindow } from '../../panels/SystemInfoWindow';
import { UrlMonitoringWindow } from '../../panels/UrlMonitoringWindow';
import { TaskManagerWindow } from '../../panels/TaskManagerWindow';
import { ServicesWindow } from '../../panels/ServicesWindow';
import { PowerShellWindow } from '../../panels/PowerShellWindow';

interface PanelMeta {
  label: string;
  Icon: typeof FolderOpenIcon;
  color: string;
}

const PANEL_META: Record<PanelType, PanelMeta> = {
  fileExplorer: { label: 'File Explorer', Icon: FolderOpenIcon, color: '#3B82F6' },
  desktop: { label: 'Desktop', Icon: DesktopMacIcon, color: '#8B5CF6' },
  webcam: { label: 'Webcam', Icon: VideocamIcon, color: '#EC4899' },
  mic: { label: 'Microphone', Icon: MicIcon, color: '#F59E0B' },
  systemInfo: { label: 'System Info', Icon: MonitorHeartIcon, color: '#10B981' },
  urlMonitoring: { label: 'URL Monitoring', Icon: LinkIcon, color: '#0EA5E9' },
  settings: { label: 'Settings', Icon: SettingsIcon, color: '#6B7280' },
  // Bloc task-manager-services 2026-05-07
  taskManager: { label: 'Task Manager', Icon: ListAltIcon, color: '#F97316' },
  services: { label: 'Services', Icon: BuildIcon, color: '#14B8A6' },
  // Bloc remote-powershell-shell 2026-05-08
  powerShell: { label: 'PowerShell', Icon: TerminalIcon, color: '#1F2937' },
};

export function WindowManager(): JSX.Element {
  const windowStack = useWindowStore((s) => s.windowStack);
  const openWindows = useWindowStore((s) => s.openWindows);

  const { data: agents } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
    refetchInterval: 5_000,
    refetchOnWindowFocus: false,
  });

  // Toggle canvas overflow when any window is out of viewport (after browser resize) so the
  // user can scroll to reach them. Constrain happens only on user drag/resize, not here.
  useEffect(() => {
    const canvas = document.querySelector('.argus-dashboard-canvas') as HTMLElement | null;
    if (!canvas) return;
    const recompute = () => {
      let overflows = false;
      for (const w of openWindows.values()) {
        if (w.x + w.width > canvas.clientWidth || w.y + w.height > canvas.clientHeight) {
          overflows = true;
          break;
        }
      }
      canvas.style.overflow = overflows ? 'auto' : 'hidden';
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [openWindows]);

  const agentsById = useMemo(() => {
    const m = new Map<string, Agent>();
    if (agents) for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%', '& > *': { pointerEvents: 'auto' } }}>
        {windowStack.map((key) => {
          const win = openWindows.get(key);
          if (!win) return null;
          // RENDER chiar și când minimized — animație CSS pe Paper (translate-to-toolbar + scale 0).
          // Pointer-events:none + opacity:0 face fereastra invizibilă + non-interactive.
          const meta = PANEL_META[win.panelType];
          const agent = agentsById.get(win.agentId);
          const agentLabel = agent?.pcName || agent?.hostname || win.agentId.substring(0, 8);
          const Icon = meta.Icon;
          return (
            <FloatingWindow
              key={key}
              windowKey={key}
              title={`${meta.label} — ${agentLabel}`}
              icon={<Icon sx={{ fontSize: 18 }} />}
              iconColor={meta.color}
              x={win.x}
              y={win.y}
              width={win.width}
              height={win.height}
              zOrder={win.zOrder}
              isMaximized={win.isMaximized}
              isMinimized={win.isMinimized}
            >
              <PanelContent panelType={win.panelType} agentId={win.agentId} agentOnline={agent?.isOnline ?? false} />
            </FloatingWindow>
          );
        })}
      </Box>
    </Box>
  );
}

interface PanelContentProps {
  panelType: PanelType;
  agentId: string;
  agentOnline: boolean;
}

function PanelContent({ panelType, agentId, agentOnline }: PanelContentProps): JSX.Element {
  if (panelType === 'fileExplorer') {
    return <FileExplorerWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'desktop') {
    return <DesktopWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'webcam' || panelType === 'mic') {
    return <WebcamWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'settings') {
    return <SettingsWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'systemInfo') {
    return <SystemInfoWindow agentId={agentId} />;
  }
  if (panelType === 'urlMonitoring') {
    return <UrlMonitoringWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'taskManager') {
    return <TaskManagerWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'services') {
    return <ServicesWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  if (panelType === 'powerShell') {
    return <PowerShellWindow agentId={agentId} agentOnline={agentOnline} />;
  }
  return <PanelStub panelType={panelType} agentId={agentId} />;
}

function PanelStub({ panelType, agentId }: { panelType: PanelType; agentId: string }): JSX.Element {
  const meta = PANEL_META[panelType];
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="h6" sx={{ color: meta.color, mb: 1 }}>
        {meta.label}
      </Typography>
      <Typography variant="caption">Panel not implemented yet.</Typography>
      <Typography variant="caption" sx={{ mt: 1, opacity: 0.6 }}>
        agent: {agentId.substring(0, 16)}…
      </Typography>
    </Box>
  );
}
